import { useState } from 'react';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: '¿Qué es Anamnesy Soft y para quién está diseñado?',
      answer: 'Anamnesy Soft es un software SaaS de control oftalmológico desarrollado por La Millefera Tech LLC. Está diseñado específicamente para oftalmólogos, optometristas, clínicas de salud visual y centros ópticos que buscan digitalizar y optimizar su gestión clínica.',
    },
    {
      question: '¿Cuánto tiempo toma la implementación?',
      answer: 'La implementación completa se realiza en 48 horas o menos. Nuestro equipo se encarga de configurar tu cuenta, migrar tus datos existentes y capacitar a tu equipo. Puedes empezar a atender pacientes con Anamnesy Soft desde el primer día.',
    },
    {
      question: '¿Mis datos están seguros?',
      answer: 'Absolutamente. Utilizamos encriptación AES-256, cumplimos con normativas HIPAA y realizamos respaldos automáticos diarios. Tus datos y los de tus pacientes están protegidos con seguridad de grado militar en servidores certificados.',
    },
    {
      question: '¿Puedo acceder desde cualquier dispositivo?',
      answer: 'Sí. Anamnesy Soft es 100% basado en la nube, por lo que puedes acceder desde cualquier computadora, tablet o smartphone con conexión a internet. No requiere instalación de software adicional.',
    },
    {
      question: '¿Se integra con equipos oftalmológicos?',
      answer: 'Sí, Anamnesy Soft se integra con los principales equipos de diagnóstico oftalmológico como OCT, campímetros, topógrafos y autorefractómetros. También nos conectamos con laboratorios ópticos y sistemas de salud existentes.',
    },
    {
      question: '¿Qué soporte ofrecen?',
      answer: 'Ofrecemos soporte técnico 24/7 con un equipo que entiende tanto la tecnología como la oftalmología. Puedes contactarnos por chat, email o teléfono. Además, incluimos capacitación continua y actualizaciones mensuales sin costo adicional.',
    },
    {
      question: '¿Puedo gestionar múltiples consultorios?',
      answer: 'Sí. Anamnesy Soft incluye gestión multi-consultorio que te permite administrar varias sedes y profesionales desde un solo panel centralizado, con reportes individuales y consolidados.',
    },
    {
      question: '¿Habrá más productos de La Millefera Tech?',
      answer: 'Sí. Anamnesy Soft es nuestro primer producto SaaS. Estamos desarrollando soluciones para otras especialidades médicas que se integrarán en un ecosistema unificado. Suscríbete a nuestro newsletter para ser el primero en conocer los lanzamientos.',
    },
  ];

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Preguntas Frecuentes</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas <span className="font-display italic text-emerald-600">saber</span>
            </h2>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="slide-up">
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-100 rounded-xl overflow-hidden hover:border-emerald-200 transition-colors"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer group"
                >
                  <span className="text-sm font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    openIndex === index ? 'bg-emerald-500 rotate-180' : 'bg-gray-100 group-hover:bg-emerald-50'
                  }`}>
                    <i className={`ri-arrow-down-s-line text-base ${openIndex === index ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                {openIndex === index && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
